import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider,
    TablePagination,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import carteraService, { Cartera } from '../services/carteraService';

const PAGE_SIZE = 20;

interface CarteraSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (cartera: Cartera) => void;
}

const CarteraSearch: React.FC<CarteraSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [carteras, setCarteras] = useState<Cartera[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [verTodos, setVerTodos] = useState(false);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    const handleSearch = useCallback(async (pageNumber = 1) => {
        try {
            setLoading(true);
            setError(null);
            setVerTodos(false);
            const trimmed = searchTerm.trim();
            const compact = trimmed.replace(/\s+/g, '');

            if (compact !== '' && /^\d+$/.test(compact)) {
                try {
                    const cartera = await carteraService.getCartera(Number(compact));
                    setCarteras(cartera ? [cartera] : []);
                    setTotalCount(cartera ? 1 : 0);
                    if (!cartera) setError('No se encontraron carteras.');
                } catch {
                    setCarteras([]);
                    setTotalCount(0);
                    setError('No se encontraron carteras.');
                } finally {
                    setLoading(false);
                }
                return;
            }

            const response = await carteraService.getCarteras({
                searchTerm: trimmed || undefined,
                pageNumber,
                pageSize: PAGE_SIZE,
            });
            setCarteras(response.items || []);
            setTotalCount(response.totalCount ?? 0);
            if (!response.items || response.items.length === 0) {
                setError('No se encontraron carteras.');
            }
        } catch (err: any) {
            setError(`Error al buscar carteras: ${err.message || 'Error desconocido'}`);
            setCarteras([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [searchTerm]);

    const handleVerTodos = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setVerTodos(true);
            setPage(0);
            const items = await carteraService.getCarteraBancosTodos();
            setCarteras(items);
            setTotalCount(items.length);
            if (!items.length) setError('No se encontraron carteras.');
        } catch (err: any) {
            setError(`Error al cargar carteras: ${err.message || 'Error desconocido'}`);
            setCarteras([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, []);

    const handlePageChange = (_: unknown, newPage: number) => {
        setPage(newPage);
        if (!verTodos) handleSearch(newPage + 1);
    };

    const handleSelect = (cartera: Cartera) => {
        onSelect(cartera);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setCarteras([]);
        setError(null);
        setPage(0);
        setTotalCount(0);
        setVerTodos(false);
        onClose();
    };

    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setCarteras([]);
            setError(null);
            setPage(0);
            setTotalCount(0);
            setVerTodos(false);
            return;
        }
        if (!searchTerm.trim()) return;
        setPage(0);
        const handle = setTimeout(() => {
            handleSearch(1);
        }, 400);
        return () => clearTimeout(handle);
    }, [open, searchTerm, handleSearch]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' },
                },
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Cartera
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Número o nombre de cartera"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(1); }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                     <IconButton onClick={() => handleSearch()} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Button
                            variant="outlined"
                            onClick={() => handleSearch()}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                            startIcon={<SearchIcon />}
                        >
                            BUSCAR
                        </Button>
                        <Button
                            variant={verTodos ? 'contained' : 'text'}
                            onClick={handleVerTodos}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                        >
                            VER TODOS
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {carteras.length > 0 && (() => {
                    const pageItems = verTodos
                        ? carteras.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
                        : carteras;
                    return (
                        <>
                            {isSmallScreen ? (
                                <Box sx={{ maxHeight: '55vh', overflowY: 'auto', pr: 1 }}>
                                    <Stack spacing={2}>
                                        {pageItems.map((cartera) => (
                                            <Card
                                                key={cartera.nroBanco}
                                                sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                                                onClick={() => handleSelect(cartera)}
                                            >
                                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                        <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                            Nro.: {cartera.nroBanco}
                                                        </Typography>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleSelect(cartera); }}
                                                            sx={{ minWidth: 'auto', px: 2 }}
                                                        >
                                                            Seleccionar
                                                        </Button>
                                                    </Box>
                                                    <Divider sx={{ my: 1.5 }} />
                                                    <Stack spacing={1}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                Nombre
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {cartera.nombreBanco?.trim() || '-'}
                                                            </Typography>
                                                        </Box>
                                                        {cartera.tipoDeCuenta && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    Tipo de cuenta
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {cartera.tipoDeCuenta}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Stack>
                                </Box>
                            ) : (
                                <TableContainer
                                    component={Paper}
                                    sx={{ maxHeight: 360, '& .MuiTableCell-root': { py: 1.5, fontSize: '0.875rem' } }}
                                >
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>Nro.</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Nombre</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Tipo cuenta</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pageItems.map((cartera) => (
                                                <TableRow
                                                    key={cartera.nroBanco}
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => handleSelect(cartera)}
                                                >
                                                    <TableCell>{cartera.nroBanco}</TableCell>
                                                    <TableCell>{cartera.nombreBanco?.trim() || '-'}</TableCell>
                                                    <TableCell>{cartera.tipoDeCuenta || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleSelect(cartera); }}
                                                        >
                                                            Seleccionar
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                            {totalCount > PAGE_SIZE && (
                                <TablePagination
                                    component="div"
                                    count={totalCount}
                                    page={page}
                                    onPageChange={handlePageChange}
                                    rowsPerPage={PAGE_SIZE}
                                    rowsPerPageOptions={[PAGE_SIZE]}
                                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                                    sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                                />
                            )}
                        </>
                    );
                })()}

                {!loading && carteras.length === 0 && searchTerm && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron carteras
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CarteraSearch;
